import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useAppConfig } from '../contexts/AppConfigContext';
import {
    Dialog, DialogContent, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import {
    Gift, MessageSquare, Zap, Handshake, ShieldCheck, X, TrendingUp,
} from 'lucide-react';

const SUNSET = '#FF5733';

/**
 * PhaseAwarePopup — shown once per calendar day per paid-beta user.
 *
 * • BETA_ACTIVE=true  → "Test it. Run it. Find a flaw." storage-growth modal
 *   with two CTAs (Reward / Feedback tab), stacked spacing, icon-chip feature
 *   rows, tactile hover states.
 * • BETA_ACTIVE=false → "Grow Your Pipeline" retail growth modal, single CTA
 *   → Reward tab. Ladder callout (30% max / 15% for 5) as a compact tier row.
 *
 * Dismissal is server-tracked (POST /api/beta/session/dismiss-popup) so it
 * respects the "daily" rule across devices and browser sessions.
 */
export default function PhaseAwarePopup({ onNavigate }) {
    const { user, isPaid, initializing } = useAuth();
    const cfg = useAppConfig();
    const [open, setOpen] = useState(false);
    const [variant, setVariant] = useState('beta');

    useEffect(() => {
        // Bind directly to the core auth-state resolution: the moment the
        // session is resolved (initializing === false) AND we have a paid user,
        // record the login day and show the growth popup. No bypass flags, no
        // session skips — it renders on every login session.
        if (initializing || !user || !isPaid) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.post('/beta/session/login');
                if (cancelled) return;
                if (data?.popup?.should_show) {
                    setVariant(data.popup.variant || (cfg.beta_active ? 'beta' : 'retail'));
                    setOpen(true);
                }
            } catch (_) { /* silent — non-critical */ }
        })();
        return () => { cancelled = true; };
    }, [initializing, user, isPaid, cfg.beta_active]);

    const dismiss = async () => {
        setOpen(false);
        try { await api.post('/beta/session/dismiss-popup'); } catch (_) { /* noop */ }
    };

    const goto = async (tab) => {
        await dismiss();
        onNavigate?.(tab);
    };

    if (!open) return null;
    return variant === 'beta'
        ? <BetaModal onDismiss={dismiss} onGoto={goto} />
        : <RetailModal onDismiss={dismiss} onGoto={goto} />;
}

/* ------------------------------------------------------------------ */
/*  Shared building blocks                                             */
/* ------------------------------------------------------------------ */

function CloseX({ onClick }) {
    return (
        <button
            data-testid="daily-popup-close-btn"
            onClick={onClick}
            aria-label="Close"
            className="
                group absolute right-5 top-5 z-10
                h-9 w-9 grid place-items-center rounded-full
                bg-background/80 backdrop-blur-sm border border-border
                text-foreground/70 hover:text-white hover:bg-[#FF5733] hover:border-[#FF5733]
                transition-all duration-200 hover:rotate-90 hover:scale-105
                focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5733]/60
            "
        >
            <X className="h-4 w-4" strokeWidth={2.4} />
        </button>
    );
}

function FeatureRow({ icon: Icon, label, amount, description }) {
    return (
        <div className="group flex items-start gap-4 py-4 px-4 sm:px-5 -mx-4 sm:-mx-5 rounded-lg transition-colors hover:bg-[#FF5733]/[0.04]">
            <div
                className="mt-0.5 h-11 w-11 shrink-0 grid place-items-center rounded-xl border transition-transform group-hover:scale-105"
                style={{
                    backgroundColor: `${SUNSET}12`,
                    borderColor: `${SUNSET}40`,
                }}
            >
                <Icon className="h-5 w-5" style={{ color: SUNSET }} strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
                <p className="font-heading text-[15px] leading-snug text-foreground">
                    {label}
                </p>
                <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                    Earn up to{' '}
                    <span
                        className="font-mono text-[13px] font-medium tracking-tight"
                        style={{ color: SUNSET }}
                    >
                        {amount}
                    </span>{' '}
                    {description}
                </p>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Beta variant                                                       */
/* ------------------------------------------------------------------ */

function BetaModal({ onDismiss, onGoto }) {
    return (
        <Dialog open={true} onOpenChange={(o) => !o && onDismiss()}>
            <DialogContent
                data-testid="daily-popup-beta"
                hideClose
                className="max-w-xl p-0 gap-0 overflow-hidden border border-[#FF5733]/30 bg-card shadow-2xl shadow-[#FF5733]/10"
            >
                <CloseX onClick={onDismiss} />

                {/* Header band with subtle sunset glow */}
                <div
                    className="px-8 pt-9 pb-6 relative"
                    style={{
                        background: `linear-gradient(180deg, ${SUNSET}0F 0%, transparent 100%)`,
                    }}
                >
                    <div className="flex items-center gap-2.5">
                        <span
                            className="inline-block h-2 w-2 rounded-full animate-pulse"
                            style={{ backgroundColor: SUNSET }}
                        />
                        <p
                            className="text-[10px] uppercase tracking-[0.32em] font-medium"
                            style={{ color: SUNSET }}
                        >
                            Founding Beta Perk
                        </p>
                    </div>
                    <DialogTitle className="mt-5 font-heading text-[26px] sm:text-[28px] leading-[1.15] tracking-tight">
                        Test it. Run it. Find a flaw. 🚀
                    </DialogTitle>
                    <DialogDescription className="mt-4 text-[14px] leading-[1.65] text-foreground/85">
                        Help us build the most unbreakable pipe on the planet.
                        Log insights, share the network, and lock in up to{' '}
                        <span className="font-medium text-foreground">600 GB</span> of free space
                        to max out your system at{' '}
                        <span className="font-medium" style={{ color: SUNSET }}>1 TB</span>.
                    </DialogDescription>
                </div>

                {/* Feature rows */}
                <div className="px-8 pb-2">
                    <div className="divide-y divide-border/40">
                        <FeatureRow
                            icon={Zap}
                            label="The Feedback Loop"
                            amount="+200 GB"
                            description="by deep-testing the engine pipeline."
                        />
                        <FeatureRow
                            icon={Handshake}
                            label="The Referral Loop"
                            amount="+400 GB"
                            description="by inviting fellow creators."
                        />
                        <FeatureRow
                            icon={ShieldCheck}
                            label="The 1-Month Safety Vault"
                            amount="30-day carry"
                            description="on all earned storage & rewards upon selecting any paid retail tier."
                        />
                    </div>
                </div>

                {/* Action buttons */}
                <div className="px-8 pt-6 pb-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Button
                        data-testid="daily-popup-invite-btn"
                        onClick={() => onGoto('rewards')}
                        className="
                            group relative h-12 text-white font-medium tracking-[0.02em]
                            transition-all duration-200
                            hover:-translate-y-0.5 hover:shadow-lg
                            active:translate-y-0
                        "
                        style={{
                            backgroundColor: SUNSET,
                            boxShadow: `0 0 0 0 ${SUNSET}00`,
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.boxShadow = `0 8px 28px ${SUNSET}55`}
                        onMouseLeave={(e) => e.currentTarget.style.boxShadow = `0 0 0 0 ${SUNSET}00`}
                    >
                        <Gift className="h-4 w-4 mr-2 transition-transform group-hover:scale-110" />
                        Invite Friends
                    </Button>
                    <Button
                        data-testid="daily-popup-feedback-btn"
                        onClick={() => onGoto('feedback')}
                        variant="outline"
                        className="
                            group h-12 font-medium tracking-[0.02em]
                            border-[#FF5733]/60 text-[#FF5733]
                            hover:bg-[#FF5733] hover:text-white hover:border-[#FF5733]
                            transition-all duration-200
                            hover:-translate-y-0.5
                        "
                    >
                        <MessageSquare className="h-4 w-4 mr-2 transition-transform group-hover:scale-110" />
                        Log Feedback
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/* ------------------------------------------------------------------ */
/*  Retail variant                                                     */
/* ------------------------------------------------------------------ */

function RetailModal({ onDismiss, onGoto }) {
    return (
        <Dialog open={true} onOpenChange={(o) => !o && onDismiss()}>
            <DialogContent
                data-testid="daily-popup-retail"
                hideClose
                className="max-w-xl p-0 gap-0 overflow-hidden border border-[#FF5733]/30 bg-card shadow-2xl shadow-[#FF5733]/10"
            >
                <CloseX onClick={onDismiss} />

                <div
                    className="px-8 pt-9 pb-6 relative"
                    style={{
                        background: `linear-gradient(180deg, ${SUNSET}0F 0%, transparent 100%)`,
                    }}
                >
                    <div className="flex items-center gap-2.5">
                        <span
                            className="inline-block h-2 w-2 rounded-full animate-pulse"
                            style={{ backgroundColor: SUNSET }}
                        />
                        <p
                            className="text-[10px] uppercase tracking-[0.32em] font-medium"
                            style={{ color: SUNSET }}
                        >
                            Referral Reward
                        </p>
                    </div>
                    <DialogTitle className="mt-5 font-heading text-[26px] sm:text-[28px] leading-[1.15] tracking-tight">
                        Grow Your Pipeline 💸
                    </DialogTitle>
                    <DialogDescription className="mt-4 text-[14px] leading-[1.65] text-foreground/85">
                        Spread the network. Grow your pipeline. Get up to{' '}
                        <span className="font-medium" style={{ color: SUNSET }}>30% OFF</span>{' '}
                        your next month!
                    </DialogDescription>
                </div>

                <div className="px-8 pb-2">
                    <p className="text-[14px] leading-[1.7] text-foreground/85 pt-1">
                        Bring{' '}
                        <span className="font-medium text-foreground">10 friends</span>{' '}
                        into the platform this billing cycle. The exact moment your 10th
                        invitee activates a paid retail plan, your next monthly subscription
                        invoice drops by a flat{' '}
                        <span className="font-medium" style={{ color: SUNSET }}>30%</span>{' '}
                        automatically.
                    </p>

                    <p className="mt-4 text-[14px] leading-[1.7] text-foreground/85">
                        Let&apos;s build together!
                    </p>

                    {/* Compact tier ladder */}
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <TierChip
                            label="5 friends"
                            percent="15% OFF"
                            hint="tier 1"
                        />
                        <TierChip
                            label="10 friends"
                            percent="30% OFF"
                            hint="max tier"
                            emphasize
                        />
                    </div>
                </div>

                <div className="px-8 pt-7 pb-8">
                    <Button
                        data-testid="daily-popup-invite-btn"
                        onClick={() => onGoto('rewards')}
                        className="
                            group w-full h-12 text-white font-medium tracking-[0.02em]
                            transition-all duration-200
                            hover:-translate-y-0.5 hover:shadow-lg
                            active:translate-y-0
                        "
                        style={{ backgroundColor: SUNSET }}
                        onMouseEnter={(e) => e.currentTarget.style.boxShadow = `0 8px 28px ${SUNSET}55`}
                        onMouseLeave={(e) => e.currentTarget.style.boxShadow = `0 0 0 0 ${SUNSET}00`}
                    >
                        <Gift className="h-4 w-4 mr-2 transition-transform group-hover:scale-110" />
                        Invite Friends
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function TierChip({ label, percent, hint, emphasize = false }) {
    return (
        <div
            className={`
                group flex items-center gap-3 px-4 py-3 rounded-lg border transition-all
                ${emphasize ? 'bg-[#FF5733]/[0.06]' : 'bg-background'}
                hover:border-[#FF5733]/60
            `}
            style={{
                borderColor: emphasize ? `${SUNSET}66` : 'rgba(255,255,255,0.10)',
            }}
        >
            <TrendingUp
                className="h-4 w-4 shrink-0"
                style={{ color: emphasize ? SUNSET : 'currentColor', opacity: emphasize ? 1 : 0.6 }}
            />
            <div className="min-w-0 flex-1">
                <p className="font-heading text-[15px]" style={{ color: emphasize ? SUNSET : undefined }}>
                    {percent}
                </p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-0.5">
                    {label} · {hint}
                </p>
            </div>
        </div>
    );
}
